import { A11yModule } from "@angular/cdk/a11y";
import { CommonModule, DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
	Component,
	ElementRef,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";

import {
	ImageLightboxData,
	ImageLightboxService,
} from "./image-lightbox.service";

@Component({
	selector: "app-image-lightbox",
	templateUrl: "./image-lightbox.component.html",
	styleUrls: ["./image-lightbox.component.scss"],
	standalone: true,
	imports: [CommonModule, A11yModule],
})
export class ImageLightboxComponent implements OnInit, OnDestroy {
	@ViewChild("lightboxDialog")
	private lightboxDialog?: ElementRef<HTMLElement>;

	image: ImageLightboxData | null = null;
	fullscreenSupported = false;
	isFullscreen = false;

	private imageSubscription?: Subscription;
	private originalBodyOverflow = "";
	private originalDocumentOverflow = "";
	private scrollLocked = false;
	private readonly isBrowser: boolean;

	constructor(
		readonly lightbox: ImageLightboxService,
		@Inject(DOCUMENT) private readonly document: Document,
		@Inject(PLATFORM_ID) platformId: object
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	ngOnInit(): void {
		this.fullscreenSupported =
			this.isBrowser &&
			Boolean(
				this.document.fullscreenEnabled &&
				this.document.documentElement.requestFullscreen
			);

		this.imageSubscription = this.lightbox.image$.subscribe((image) => {
			this.image = image;

			if (image) {
				this.lockDocumentScroll();
			} else {
				this.unlockDocumentScroll();
			}
		});
	}

	ngOnDestroy(): void {
		this.imageSubscription?.unsubscribe();
		this.unlockDocumentScroll();
	}

	@HostListener("document:keydown.escape", ["$event"])
	onEscape(event: KeyboardEvent): void {
		if (!this.image) {
			return;
		}

		event.preventDefault();
		void this.close();
	}

	@HostListener("document:fullscreenchange")
	onFullscreenChange(): void {
		this.isFullscreen = Boolean(this.document.fullscreenElement);
	}

	onBackdropClick(event: MouseEvent): void {
		if (event.target === event.currentTarget) {
			void this.close();
		}
	}

	async close(): Promise<void> {
		try {
			await this.exitLightboxFullscreen();
		} finally {
			this.lightbox.close();
		}
	}

	async toggleFullscreen(): Promise<void> {
		if (!this.fullscreenSupported || !this.lightboxDialog) {
			return;
		}

		try {
			if (this.document.fullscreenElement) {
				await this.document.exitFullscreen();
				return;
			}

			await this.lightboxDialog.nativeElement.requestFullscreen();
		} catch {
			this.isFullscreen = Boolean(this.document.fullscreenElement);
		}
	}

	private async exitLightboxFullscreen(): Promise<void> {
		const fullscreenElement = this.document.fullscreenElement;
		const dialog = this.lightboxDialog?.nativeElement;

		if (
			this.fullscreenSupported &&
			fullscreenElement &&
			dialog &&
			(fullscreenElement === dialog || dialog.contains(fullscreenElement))
		) {
			await this.document.exitFullscreen();
		}
	}

	private lockDocumentScroll(): void {
		if (!this.isBrowser || this.scrollLocked) {
			return;
		}

		this.originalBodyOverflow = this.document.body.style.overflow;
		this.originalDocumentOverflow =
			this.document.documentElement.style.overflow;
		this.document.body.style.overflow = "hidden";
		this.document.documentElement.style.overflow = "hidden";
		this.scrollLocked = true;
	}

	private unlockDocumentScroll(): void {
		if (!this.isBrowser || !this.scrollLocked) {
			return;
		}

		this.document.body.style.overflow = this.originalBodyOverflow;
		this.document.documentElement.style.overflow =
			this.originalDocumentOverflow;
		this.scrollLocked = false;
	}
}
